import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, X, Lock, Camera } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSTES = ["Gardien", "Défenseur central", "Latéral droit", "Latéral gauche", "Milieu défensif", "Milieu central", "Milieu offensif", "Ailier droit", "Ailier gauche", "Attaquant"];
const MAILLOTS = ["S", "M", "L", "XL", "XXL"];

const initial = {
  prenom: "", nom: "", genre: "", date_naissance: "", poste_principal: "", poste_secondaire: "",
  taille: "", poids: "", club_actuel: "", email: "", telephone: "", video_url: "", taille_maillot: "",
};

export default function Registration({ detection }) {
  const closed = detection ? !detection.inscriptions_ouvertes : false;
  const [form, setForm] = useState(initial);
  const [methode, setMethode] = useState("stripe");
  const [accepte, setAccepte] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoRef = useRef(null);
  const { t } = useLang();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload-public`, fd);
      setPhoto(data.url);
      toast.success(t("reg.photo.ok"));
    } catch {
      toast.error(t("reg.photo.error"));
    } finally {
      setPhotoUploading(false);
    }
  };

  // Catégorie (U19/U23 × genre) — peut être close par l'admin quand le quota est atteint
  const year = parseInt((form.date_naissance || "").slice(0, 4), 10);
  const catKey = form.genre && form.date_naissance ? `${year >= 2008 ? "u19" : "u23"}${form.genre === "féminin" ? "f" : "m"}` : null;
  const catFermee = !!(catKey && (detection?.categories_fermees || []).includes(catKey));

  const submit = async (e) => {
    e.preventDefault();
    if (!accepte) {
      toast.error(t("reg.toast.terms"));
      return;
    }
    if (catFermee) {
      toast.error(t("reg.closed.category"));
      return;
    }
    setLoading(true);
    try {
      const { data: reg } = await axios.post(`${API}/registrations`, {
        ...form,
        poste_secondaire: form.poste_secondaire || null,
        taille: form.taille || null,
        poids: form.poids || null,
        club_actuel: form.club_actuel || null,
        video_url: form.video_url || null,
        photo: photo || null,
        methode_paiement: methode,
        accepte_conditions: accepte,
        detection_id: detection?.id || "kinshasa-2026",
      });
      if (methode === "mobile_money") {
        toast.success(t("reg.toast.saved"), { description: t("reg.toast.mm") });
        window.location.href = `/paiement/mobile-money?id=${reg.id}`;
        return;
      }
      if (methode === "bureau") {
        toast.success(t("reg.toast.saved"), { description: t("reg.toast.office") });
        window.location.href = `/paiement/bureau?id=${reg.id}`;
        return;
      }
      const { data: checkout } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "detection_fee",
        origin_url: window.location.origin,
        registration_id: reg.id,
      });
      toast.success(t("reg.toast.saved"), { description: t("resume.loading") });
      window.location.href = checkout.checkout_url;
    } catch {
      toast.error(t("reg.toast.error"), { description: t("reg.toast.error.x") });
      setLoading(false);
    }
  };

  return (
    <section id="detection" data-testid="registration-section" className="py-28 lg:py-40 bg-[#121214] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("reg.eyebrow")}</p>
          <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
            {t("reg.title.a")} <span className="text-[#C5A059]">{t("reg.title.b")}</span>
          </h2>
          <p className="text-lg leading-relaxed text-[#A1A1AA] mt-8">
            {t("reg.text")}
          </p>
          <ul className="mt-10 space-y-4">
            {[t("reg.b1"), t("reg.b2"), t("reg.b3"), t("reg.b4"), t("reg.b5"), t("reg.b6")].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                <span className="w-1.5 h-1.5 bg-[#C5A059] mt-2 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="font-editorial italic text-[#71717A] mt-12 text-lg">
            "{t("reg.quote")}"
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 bg-[#0A0A0C] border border-[#27272A] p-8 lg:p-12"
          data-testid="registration-form"
        >
          {closed ? (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center" data-testid="registration-closed">
              <Lock size={36} className="text-[#C5A059]" strokeWidth={1.5} />
              <p className="font-display text-2xl uppercase font-bold mt-6">{t("reg.closed.t")}</p>
              <p className="text-sm text-[#A1A1AA] mt-4 max-w-md leading-relaxed">{t("reg.closed.x")}</p>
              <p className="font-editorial italic text-[#71717A] mt-8">"{t("reg.quote")}"</p>
            </div>
          ) : (
          <>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
            <input data-testid="reg-input-prenom" required placeholder={t("reg.f.firstname")} value={form.prenom} onChange={set("prenom")} className="input-editorial" />
            <input data-testid="reg-input-nom" required placeholder={t("reg.f.lastname")} value={form.nom} onChange={set("nom")} className="input-editorial" />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("reg.f.birthdate")}</label>
              <input data-testid="reg-input-date-naissance" required type="date" value={form.date_naissance} onChange={set("date_naissance")} className="input-editorial [color-scheme:dark]" />
            </div>
            <select data-testid="reg-select-genre" required value={form.genre} onChange={set("genre")} className="input-editorial">
              <option value="" disabled>{t("reg.f.gender")}</option>
              <option value="masculin">{t("reg.f.gender.m")}</option>
              <option value="féminin">{t("reg.f.gender.f")}</option>
            </select>
            <select data-testid="reg-select-poste-principal" required value={form.poste_principal} onChange={set("poste_principal")} className="input-editorial">
              <option value="" disabled>{t("reg.f.position")}</option>
              {POSTES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="reg-select-poste-secondaire" value={form.poste_secondaire} onChange={set("poste_secondaire")} className="input-editorial">
              <option value="">{t("reg.f.position2")}</option>
              {POSTES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="reg-select-taille-maillot" required value={form.taille_maillot} onChange={set("taille_maillot")} className="input-editorial">
              <option value="" disabled>{t("reg.f.jersey")}</option>
              {MAILLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-8">
              <input data-testid="reg-input-taille" placeholder={t("reg.f.height")} value={form.taille} onChange={set("taille")} className="input-editorial" />
              <input data-testid="reg-input-poids" placeholder={t("reg.f.weight")} value={form.poids} onChange={set("poids")} className="input-editorial" />
            </div>
            <input data-testid="reg-input-club" placeholder={t("reg.f.club")} value={form.club_actuel} onChange={set("club_actuel")} className="input-editorial" />
            <input data-testid="reg-input-telephone" required type="tel" placeholder={t("reg.f.phone")} value={form.telephone} onChange={set("telephone")} className="input-editorial" />
            <input data-testid="reg-input-email" required type="email" placeholder={t("reg.f.email")} value={form.email} onChange={set("email")} className="input-editorial sm:col-span-2" />
            <input data-testid="reg-input-video" type="url" placeholder={t("reg.f.video")} value={form.video_url} onChange={set("video_url")} className="input-editorial sm:col-span-2" />
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("reg.f.photo")}</label>
              <div className="flex items-center gap-4 mt-2">
                <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" data-testid="reg-photo-input" />
                <button
                  type="button"
                  onClick={() => photoRef.current?.click()}
                  disabled={photoUploading}
                  data-testid="reg-photo-button"
                  className="flex items-center gap-2 border border-[#27272A] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300 disabled:opacity-50"
                >
                  {photoUploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                  {photo ? t("reg.photo.change") : t("reg.photo.cta")}
                </button>
                {photo && (
                  <img src={photo} alt="Photo de profil" className="h-14 w-14 object-cover border border-[#C5A059]/50" data-testid="reg-photo-preview" />
                )}
              </div>
            </div>
          </div>

          <div className="mt-10">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-4" data-testid="reg-pay-title">
              {t("reg.pay.title")} <span className="text-[#C5A059]">— {t("reg.pay.fee")}</span>
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => setMethode("stripe")}
                data-testid="pay-method-stripe"
                className={`text-left border p-5 transition-colors duration-300 ${methode === "stripe" ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#27272A] hover:border-[#3f3f46]"}`}
              >
                <p className="font-display uppercase font-medium">{t("reg.pay.card.t")}</p>
                <p className="text-[11px] text-[#71717A] mt-1">{t("reg.pay.card.x")}</p>
              </button>
              <button
                type="button"
                onClick={() => setMethode("mobile_money")}
                data-testid="pay-method-mobile"
                className={`text-left border p-5 transition-colors duration-300 ${methode === "mobile_money" ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#27272A] hover:border-[#3f3f46]"}`}
              >
                <p className="font-display uppercase font-medium">{t("reg.pay.mm.t")}</p>
                <p className="text-[11px] text-[#71717A] mt-1">{t("reg.pay.mm.x")}</p>
              </button>
              <button
                type="button"
                onClick={() => setMethode("bureau")}
                data-testid="pay-method-bureau"
                className={`text-left border p-5 transition-colors duration-300 ${methode === "bureau" ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#27272A] hover:border-[#3f3f46]"}`}
              >
                <p className="font-display uppercase font-medium">{t("reg.pay.office.t")}</p>
                <p className="text-[11px] text-[#71717A] mt-1">{t("reg.pay.office.x")}</p>
              </button>
            </div>
          </div>

          <p className="text-[11px] text-[#71717A] mt-8 leading-relaxed">
            {t("reg.fee")}
          </p>

          <div className="mt-8 flex items-start gap-3">
            <input
              type="checkbox"
              id="reg-terms"
              required
              checked={accepte}
              onChange={(e) => setAccepte(e.target.checked)}
              data-testid="reg-terms-checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059] cursor-pointer"
            />
            <label htmlFor="reg-terms" className="text-xs text-[#A1A1AA] leading-relaxed cursor-pointer">
              {t("reg.terms.label")}{" "}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                data-testid="reg-terms-open"
                className="text-[#C5A059] underline underline-offset-2 hover:text-[#E5C07B] transition-colors"
              >
                {t("reg.terms.link")}
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || catFermee}
            data-testid="reg-submit-button"
            className="group mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-black px-10 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
            {methode === "stripe" ? t("reg.submit.card") : methode === "mobile_money" ? t("reg.submit.mm") : t("reg.submit.office")}
          </button>
          {catFermee && (
            <p data-testid="reg-cat-fermee" className="mt-4 text-amber-400 text-sm border border-amber-400/40 bg-amber-400/5 px-4 py-3">{t("reg.closed.category")}</p>
          )}
          </>
          )}
        </motion.form>
      </div>

      {termsOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" role="dialog" aria-modal="true" data-testid="terms-modal">
          <div className="absolute inset-0 bg-black/85" onClick={() => setTermsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-2xl w-full max-h-[80vh] overflow-y-auto bg-[#121214] border border-[#C5A059]/40 p-8 lg:p-10"
          >
            <button
              onClick={() => setTermsOpen(false)}
              data-testid="terms-close-button"
              className="absolute top-5 right-5 text-[#71717A] hover:text-[#C5A059] transition-colors"
              aria-label={t("reg.terms.close")}
            >
              <X size={18} />
            </button>
            <p className="text-[#C5A059] text-[10px] uppercase tracking-[0.4em] mb-4">Dream2Reality Football Agency</p>
            <h3 className="font-display text-2xl uppercase font-bold pr-8">{t("reg.terms.title")}</h3>
            <p className="text-sm text-[#A1A1AA] leading-relaxed mt-6 whitespace-pre-line">{t("reg.terms.body")}</p>
            <button
              onClick={() => setTermsOpen(false)}
              data-testid="terms-accept-close"
              className="mt-8 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300"
            >
              {t("reg.terms.close")}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
    </section>
  );
}
